import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  SimpleChange,
  ViewChild,
} from "@angular/core";
import Chart from "chart.js/auto";

import { GroupedChart } from "../../../common/models/grouped-chart";
import { Currency } from "../../../currency/models/currency.model";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "mojo-cash-balance-chart",
  templateUrl: "./cash-balance-chart.component.html",
  styleUrls: ["./cash-balance-chart.component.scss"],
})
export class CashBalanceChartComponent implements OnInit {
  @ViewChild("chartCanvas", { static: true })
  chartCanvas: ElementRef;
  @Input()
  loading: boolean;
  @Input()
  cashBalanceChartData: GroupedChart;
  @Input()
  chartFontColour: string;
  @Input()
  chartGridLineColour: string;
  @Input()
  chartColours: Array<string>;
  @Input()
  currentCurrency: Currency;

  private chart: any;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
  ) {}

  get currencySymbol(): string {
    return this.currentCurrency ? this.currentCurrency.Symbol : "";
  }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChange) {
    if (changes["cashBalanceChartData"] && this.cashBalanceChartData) {
      setTimeout(() => {
        this.createChart();
      }, 333);
    }
  }

  ngOnDestroy() {
    this.destroyChartIfRequired();
  }

  public createChart() {
    this.destroyChartIfRequired();
    this.changeDetector.markForCheck();
    this.chart = new Chart(
      this.createChartContext(),
      this.createBarChartData(),
    );
  }

  private destroyChartIfRequired() {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  private createChartContext() {
    return this.chartCanvas.nativeElement.getContext("2d");
  }

  private createBarChartData(): any {
    Chart.defaults.color = this.chartFontColour;
    var chartData = {
      type: "bar",
      data: this.cashBalanceChartData,

      options: {
        maintainAspectRatio: false,
        responsive: true,
        display: true,

        plugins: {
          title: {
            display: false,
          },
          legend: {
            position: "bottom",
            labels: {
              color: this.chartFontColour,
            },
          },
          tooltip: {
            callbacks: {
              title: (context) => context[0].label,
              label: (context) => this.tooltipToCurrencyFormat(context),
            },
          },
        },

        scales: {
          xAxes: {
            scaleLabel: {
              display: true,
            },
            ticks: {
              color: this.chartFontColour,
            },
            stacked: true,
          },
          yAxes: {
            ticks: {
              color: this.chartFontColour,
              callback: (value) => this.tooltipToCurrencyFormat(value),
            },
            stacked: true,
            grid: {
              color: this.chartGridLineColour,
              drawBorder: false,
            },
          },
        },
      },
    };
    return chartData;
  }

  /**
   * format the tooltip text to appear as currency format
   */
  private tooltipToCurrencyFormat(context: any): string {
    const formatted: string = `${this.currencySymbol}${
      this.formatDecimal(context.parsed ? context.parsed.y : context as number)
    }`;
    return formatted;
  }

  private formatDecimal(value: number): string {
    return value.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
  }
}
